import { useContext, useEffect, useState, useCallback, useRef } from 'react';
import './style.css';

// Components
import SellModule from '../../organisms/sellModule/view';
import PayMethods from '../../organisms/paymethods/view';
import ProductsTable from '../../organisms/productsTable/view';
import ButtonGroup from '../../molecules/buttonGroup/view';
import CreateReceipt from '../../organisms/CreateReceipt/view';
import DailyClosureModal from '../../organisms/DailyClosureModal/view';
import TabSwitcher from '../../molecules/TabSwitcher/view';
import Table from '../../molecules/tablas/view';
import CustomBox from '../../atoms/container/container';
import ButtonAction from '../../atoms/buttonAction/view';
import TooltipC from '../../atoms/tooltip/view';
import LoadingView from '../../pages/loading/loading-view';
import ConfirmPopUp from '../../atoms/confirmPopUp/view';
import Modal from '../../atoms/modal/view';
import ThirdForm from '../../organisms/thirdForm/view';
import { getThirdById, getThirdByIdentification, editThird } from '../../../usecases/thirds-usecase';

// Context & Utils
import { roleContext } from '../../../context/role/context';
import { listener, dispatchCustomEvent } from '../../../utils/dispatch';
import { useCarrito } from '../../../context/carrito/context';
import { useAskPrice } from '../../atoms/prompt/price';
import { useUpdateCarItem } from '../../atoms/prompt/caritem';
import { useNavigationIntent } from '../../../hooks/useNavigationIntent';

// Icons & Models
import { BiErrorCircle } from 'react-icons/bi';
import { BsArrowBarLeft, BsArrowLeft, BsArrowRepeat, BsBack } from 'react-icons/bs';

// Controller
import {
  getInvoiceList,
  getConflictedSalesList,
  handleResolveConflict,
  submitCreditNote,
} from './controller';
import InvoicesTable from '../../organisms/InvoicesTable/view';
import { Col, Row } from 'react-bootstrap';
import { getAllProducts } from '../../../usecases/products-usecase';

function Pos() {
  const { profile, isElectronicInvoiceEnable } = useContext(roleContext);
  const roleId = profile?.colaborador?.role_id ?? profile?.role_id;
  const isCashier = roleId === 3;
  const { askPrice, PriceModalComponent } = useAskPrice();
  const { askUpdateCarItem, UpdateCarItemModalComponent } = useUpdateCarItem();

  // Context Cart
  const {
    car,
    taxGroup,
    total,
    addItem,
    removeItem,
    updateItem: updateCartItem,
    cloneItem: cloneCartItem,
  } = useCarrito();

  // Local State
  const [tercero, setTercero] = useState(undefined);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const intent = useNavigationIntent();

  useEffect(() => {
    if (intent && intent.tabIndex !== undefined) {
      setActiveTabIndex(intent.tabIndex);
    }
  }, [intent]);
  const [isLoading, setIsLoading] = useState(false);
  const [invoices, setInvoices] = useState({
    data: [],
    page: 1,
    page_size: 8,
    total: 0,
  });
  const [conflicts, setConflicts] = useState({
    data: [],
    page: 1,
    page_size: 8,
    total: 0,
  });
  const [isLoadingConflicts, setIsLoadingConflicts] = useState(true);
  const [selectedThirdForConflict, setSelectedThirdForConflict] = useState(null);
  const [conflictBillId, setConflictBillId] = useState(null);

  // Callback para resolver conflictos
  const onResolveClick = async (row, force = false) => {
    const item = row._original || row;
    const conflictType = item.conflict_type;

    // Si es conflicto por datos de cliente y no se está forzando re-emisión
    if (conflictType === 'CLIENT_DATA' && !force && (item.thirds_id || item.customer_id)) {
      setIsLoading(true);
      try {
        let thirdData = null;
        if (item.thirds_id) {
          thirdData = await getThirdById(item.thirds_id);
        } else if (item.customer_id) {
          thirdData = await getThirdByIdentification(item.customer_id);
        }

        if (thirdData) {
          setSelectedThirdForConflict(thirdData);
          setConflictBillId(item.bill_id);
          dispatchCustomEvent('modalconflict-edit-third');
          return;
        }
      } catch (err) {
        console.warn('No se pudo cargar tercero completo, usando datos básicos:', err);
        setSelectedThirdForConflict({
          id: item.thirds_id,
          identification: item.customer_id,
          name: item.customer,
        });
        setConflictBillId(item.bill_id);
        dispatchCustomEvent('modalconflict-edit-third');
        return;
      } finally {
        setIsLoading(false);
      }
    }

    // Para Timeouts / Pending / Excepciones o re-emisión forzada
    setIsLoading(true);
    try {
      const result = await handleResolveConflict(item.bill_id, force);
      if (result?.requires_confirmation) {
        dispatchCustomEvent(
          'notification-confirm-pos-resolve-conflict',
          result.requires_confirmation ? item : {}
        );
      } else if (result?.success) {
        handleFetchConflicts();
      }
    } catch (error) {
      console.error('Error resolving conflict:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConflictThirdSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const thirdId = formData.id || selectedThirdForConflict?.id;
      if (thirdId) {
        await editThird(thirdId, formData);
      }
      dispatchCustomEvent('modal-close-conflict-edit-third');
      dispatchCustomEvent('popup', {
        message: 'Cliente actualizado. Re-transmitiendo factura...',
        type: 'success',
      });

      if (conflictBillId) {
        const result = await handleResolveConflict(conflictBillId, true);
        if (result?.success) {
          handleFetchConflicts();
        }
      }
    } catch (err) {
      console.error('Error al actualizar tercero:', err);
      const msg = err.response?.data?.message || 'Error al actualizar datos del cliente';
      dispatchCustomEvent('popup', { message: msg, type: 'error' });
    } finally {
      setIsLoading(false);
      setSelectedThirdForConflict(null);
      setConflictBillId(null);
    }
  };



  // ESCÁNER GLOBAL - AÑADIR AL CARRITO
  const scannerBuffer = useRef('');
  const lastScanTime = useRef(Date.now());

  useEffect(() => {
    const handlePosScan = async (e) => {
      const isInput =
        e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA';
      const currentTime = Date.now();
      const timeDiff = currentTime - lastScanTime.current;
      lastScanTime.current = currentTime;

      if (e.key === 'Enter') {
        if (scannerBuffer.current.length > 2) {
          e.preventDefault();
          e.stopPropagation();
          const code = scannerBuffer.current;
          scannerBuffer.current = '';

          try {
            const res = await getAllProducts(
              profile.uid,
              profile.cid,
              code,
              1,
              1,
              false,
              true
            );
            if (res && res.data && res.data.length > 0) {
              const product = res.data[0];
              addItem(product);
              dispatchCustomEvent('popup', {
                message: `Añadido: ${product.name}`,
                type: 'success',
                duration: 1000,
              });
            } else {
              dispatchCustomEvent('popup', {
                message: `Código ${code} no encontrado`,
                type: 'error',
              });
            }
          } catch (error) {
            console.error('Error buscando producto:', error);
          }
        }
      } else if (e.key && e.key.length === 1) {
        // En el POS, somos más agresivos: robamos incluso si estamos en un input
        // A MENOS que sea un tiempo de escritura muy humano
        if (
          !isInput ||
          (scannerBuffer.current && scannerBuffer.current.length > 0) ||
          timeDiff < 45
        ) {
          if (
            isInput &&
            timeDiff > 90 &&
            (!scannerBuffer.current || scannerBuffer.current.length === 0)
          )
            return;

          e.preventDefault();
          e.stopPropagation();
          scannerBuffer.current += e.key;

          clearTimeout(window.posScanTimeout);
          window.posScanTimeout = setTimeout(() => {
            scannerBuffer.current = '';
          }, 300);
        }
      }
    };

    document.addEventListener('keydown', handlePosScan, true);
    return () => {
      document.removeEventListener('keydown', handlePosScan, true);
      clearTimeout(window.posScanTimeout);
    };
  }, [profile.uid, profile.cid, addItem]);

  // --- Tab Facturas Logic ---
  const handleFetchInvoices = useCallback(
    async (page = invoices?.page || 1, limit = invoices?.page_size || 8) => {
      setInvoices((prev) => ({ ...prev, data: [] }));
      try {
        const result = await getInvoiceList(page, limit, {}, 'pos');
        setInvoices(result);
      } catch (error) {
        console.error('Error fetching invoices:', error);
      }
    },
    [invoices?.page, invoices?.page_size]
  );

  const handleFetchConflicts = useCallback(
    async (page = conflicts?.page || 1, limit = conflicts?.page_size || 8) => {
      setConflicts((prev) => ({ ...prev, data: [] }));
      setIsLoadingConflicts(true);
      try {
        const result = await getConflictedSalesList(page, limit, 'pos');
        setConflicts(result);
      } catch (error) {
        console.error('Error fetching conflicts:', error);
      } finally {
        setIsLoadingConflicts(false);
      }
    },
    [conflicts?.page, conflicts?.page_size]
  );

  // --- Efectos ---
  useEffect(() => {
    const onThirdSelected = (e) =>
      setTercero(e.detail ? e.detail.data : undefined);
    const onAddProduct = (e) => addItem(e.detail.data);
    const onCloneProduct = async (e) => {
      try {
        const precio = await askPrice(e.detail.data.value);
        cloneCartItem(e.detail.data, precio);
      } catch {
        /* Ignorar cancelación de prompt */
      }
    };

    const l1 = listener('btn-thirds-add', onThirdSelected);
    const l2 = listener('btn-add-product', onAddProduct);
    const l3 = listener('btn-clone-product', onCloneProduct);

    return () => {
      l1();
      l2();
      l3();
    };
  }, [addItem, askPrice, cloneCartItem]);

  useEffect(() => {
    const l1 = listener('reload-invoices', () => handleFetchInvoices());

    return () => {
      l1();
    };
  }, [handleFetchInvoices]);

  useEffect(() => {
    handleFetchConflicts();
  }, [handleFetchConflicts]);

  useEffect(() => {
    if (activeTabIndex === 1) handleFetchInvoices();
    if (activeTabIndex === 2) handleFetchConflicts();
  }, [activeTabIndex, handleFetchInvoices, handleFetchConflicts]);

  // --- Handlers de Carrito ---
  async function update(item) {
    try {
      const newData = await askUpdateCarItem(item.value, item.amount, 'amount', item.name);
      updateCartItem(item.auxsku, 'value', parseFloat(newData.value));
      updateCartItem(item.auxsku, 'amount', parseFloat(newData.amount));
    } catch {
      /* Ignorar cancelación */
    }
  }

  const tabs = [
    {
      name: 'POS',
      component: () => (
        <Row className="g-4 align-items-stretch">
          <Col md={6}>
            <CustomBox style={{ height: '100%', padding: 'var(--spacing-md)' }}>
              <ProductsTable profile={profile} type="sell" scannerMode="none" />
            </CustomBox>
          </Col>
          <Col md={6}>
            <CustomBox
              style={{
                height: '100%',
                padding: 0,
                border: 'none',
                background: 'transparent',
              }}
            >
              <SellModule
                removeItems={removeItem}
                car={car}
                update={update}
                taxGroup={taxGroup}
                tercero={tercero}
                total={total}
              >
                <PayMethods origin="pos" />
              </SellModule>
            </CustomBox>
          </Col>
        </Row>
      ),
    },
    {
      name: 'Facturas',
      disabled: !isElectronicInvoiceEnable,
      component: () => (
        <CustomBox style={{ maxHeight: '90%' }}>
          <InvoicesTable
            id="invoice-table-pos"
            invoices={invoices}
            onReload={handleFetchInvoices}
          />
        </CustomBox>
      ),
    },
    {
      name: 'Conflictos',
      badge: conflicts?.total || 0,
      disabled: false,
      component: () => (
        <CustomBox>
          <Table
            id="conflicts-table-pos"
            onReload={() => handleFetchConflicts()}
            headersList={['Fecha', 'Cliente', 'Documento', 'Total', 'Motivo / Error']}
            paginatedData={conflicts}
            getFunction={handleFetchConflicts}
            excludeList={['bill_id', 'conflict_type', 'thirds_id', 'customer_id', '_original']}
            isLoading={isLoadingConflicts}
            formats={{
              total: { type: 'money' },
              date: { type: 'timestamp' },
              error_message: {
                type: 'custom',
                children: (item) => {
                  const msg = item.error_message || 'Sin detalle de error';
                  return (
                    <TooltipC text={msg}>
                      <div className="conflict-error-pill">
                        <BiErrorCircle size={15} className="conflict-error-icon" />
                        <span className="conflict-error-text">{msg}</span>
                      </div>
                    </TooltipC>
                  );
                },
              },
            }}
          >
            <ButtonAction
              title={<BsArrowRepeat />}
              onClick={(data) => onResolveClick(data)}
            />
          </Table>
        </CustomBox>
      ),
    },
  ];

  const handleSubmitCreditNote = async (data) => {
    setIsLoading(true);
    try {
      await submitCreditNote(data);
      await handleFetchInvoices();
    } catch (error) {
      console.error('Error submitting credit note:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LoadingView />}
      <ConfirmPopUp
        eventKey="create-nota-credito"
        msg={'Quieres anular la factura creando una nota credito?'}
        onAcept={handleSubmitCreditNote}
      />
      <ConfirmPopUp
        eventKey="pos-resolve-conflict"
        msg={'¿Deseas intentar generar una nueva factura para esta venta?'}
        onAcept={(data) => onResolveClick(data, true)}
      />
      <Modal
        id="conflict-edit-third"
        onClose={() => {
          setSelectedThirdForConflict(null);
          setConflictBillId(null);
        }}
        style={{ width: '100%', maxWidth: '850px', maxHeight: '90vh' }}
      >
        {selectedThirdForConflict && (
          <ThirdForm
            initialData={selectedThirdForConflict}
            onSubmit={handleConflictThirdSubmit}
            loadingId="third-conflict-edit"
            title="Corregir Datos del Cliente para Facturación"
            submitLabel="Guardar y Re-transmitir Factura"
          />
        )}
      </Modal>
      {PriceModalComponent}
      {UpdateCarItemModalComponent}
      <ButtonGroup
        title="Punto de Venta"
        description={
          !isCashier ? (
            <p style={{ cursor: 'pointer', margin: 0 }} onClick={() => window.location.replace('/hub')}>
              <BsArrowLeft /> Volver al menu
            </p>
          ) : null
        }
      >
        <CreateReceipt />
        <DailyClosureModal />
      </ButtonGroup>
      <TabSwitcher
        tabs={tabs}
        onTabChange={setActiveTabIndex}
        activeTabIndex={activeTabIndex}
        hashKey="tab"
      />
    </>
  );
}

export default Pos;
